/**
 * SSR-safe version of the Kontakt page.
 * The contact form with tRPC hooks is loaded client-side only.
 * The static content (contact info, map, SEO) renders during SSR.
 */
import { useState, useEffect } from "react";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import AnimatedSection from "@/components/AnimatedSection";
import PageHero from "@/components/PageHero";
import { CONTACT, IMAGES } from "@/lib/constants";
import ClientOnly from "@/components/ClientOnly";

// Lazy-load the actual form component that uses tRPC
import { lazy, Suspense } from "react";
const KontaktForm = lazy(() => import("@/components/KontaktForm"));

export default function KontaktSSR() {
  return (
    <Layout>
      <SEOHead
        title="Kontakta ASPAB - Begär offert på laserskärning | Eskilstuna"
        description="Kontakta ASPAB för offert på laserskärning, kantpressning och svetsning. Ring 016-13 26 00 eller fyll i formuläret. Svar inom 24 timmar."
        canonical="https://aspab.com/kontakt"
        path="/kontakt"
        breadcrumbs={[{ name: "Kontakt", href: "/kontakt" }]}
        ogImage={IMAGES.ogKontakt}
      />

      <PageHero
        title="Kontakta oss"
        subtitle="Begär offert eller ställ en fråga. Vi svarar inom 24 timmar."
        breadcrumbs={[{ name: "Kontakt", href: "/kontakt" }]}
      />

      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Info - renders during SSR */}
            <div className="lg:col-span-2">
              <AnimatedSection>
                <h2 className="font-heading text-2xl font-bold text-gray-900 mb-6">Kontaktuppgifter</h2>

                <div className="space-y-6">
                  <a
                    href={`tel:${CONTACT.phone.replace(/[^0-9+]/g, "")}`}
                    className="flex items-start gap-4 group"
                  >
                    <div className="w-10 h-10 flex items-center justify-center bg-[#2563EB]/10 border border-[#2563EB]/20 rounded-lg shrink-0 group-hover:bg-[#2563EB]/20 transition-colors">
                      <Phone className="w-5 h-5 text-[#2563EB]" />
                    </div>
                    <div>
                      <div className="text-gray-500 text-sm">Telefon</div>
                      <div className="text-gray-900 font-medium group-hover:text-[#2563EB] transition-colors">{CONTACT.phone}</div>
                    </div>
                  </a>

                  <a
                    href={`mailto:${CONTACT.email}`}
                    className="flex items-start gap-4 group"
                  >
                    <div className="w-10 h-10 flex items-center justify-center bg-[#2563EB]/10 border border-[#2563EB]/20 rounded-lg shrink-0 group-hover:bg-[#2563EB]/20 transition-colors">
                      <Mail className="w-5 h-5 text-[#2563EB]" />
                    </div>
                    <div>
                      <div className="text-gray-500 text-sm">E-post</div>
                      <div className="text-gray-900 font-medium group-hover:text-[#2563EB] transition-colors">{CONTACT.email}</div>
                    </div>
                  </a>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#2563EB]/10 border border-[#2563EB]/20 rounded-lg shrink-0">
                      <MapPin className="w-5 h-5 text-[#2563EB]" />
                    </div>
                    <div>
                      <div className="text-gray-500 text-sm">Adress</div>
                      <div className="text-gray-900 font-medium">{CONTACT.address}</div>
                      <div className="text-gray-500">{CONTACT.city}</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#2563EB]/10 border border-[#2563EB]/20 rounded-lg shrink-0">
                      <Clock className="w-5 h-5 text-[#2563EB]" />
                    </div>
                    <div>
                      <div className="text-gray-500 text-sm">Öppettider</div>
                      <div className="text-gray-900 font-medium">Mån-Fre: 07:00 - 16:00</div>
                    </div>
                  </div>
                </div>

                {/* Map */}
                <div className="mt-8 bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2034.5!2d16.4908!3d59.3766!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x465e9b0a0a0a0a0a%3A0x0!2sAbramsons+v%C3%A4g+2%2C+632+39+Eskilstuna!5e0!3m2!1ssv!2sse!4v1"
                    width="100%"
                    height="220"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="ASPAB - Abramsons väg 2, Eskilstuna"
                    className="hover:grayscale-0 grayscale opacity-80 hover:opacity-100 transition-all duration-300"
                  />
                </div>
              </AnimatedSection>
            </div>

            {/* Form - only renders on client side */}
            <div className="lg:col-span-3">
              <AnimatedSection delay={200}>
                <div className="bg-white border border-gray-200 rounded-lg p-8 md:p-10">
                  <h2 className="font-heading text-2xl font-bold text-gray-900 mb-2">Begär offert</h2>
                  <p className="text-gray-500 mb-8">
                    Fyll i formuläret nedan och bifoga gärna ritningar direkt. Vi återkommer inom 24 timmar med en offert.
                  </p>
                  <ClientOnly
                    fallback={
                      <div className="space-y-5">
                        <div className="grid sm:grid-cols-2 gap-5">
                          <div>
                            <label className="block text-gray-500 text-sm mb-1.5">Namn *</label>
                            <input type="text" className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg" placeholder="Ditt namn" />
                          </div>
                          <div>
                            <label className="block text-gray-500 text-sm mb-1.5">Företag</label>
                            <input type="text" className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg" placeholder="Företagsnamn" />
                          </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-5">
                          <div>
                            <label className="block text-gray-500 text-sm mb-1.5">E-post *</label>
                            <input type="email" className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg" placeholder="din@epost.se" />
                          </div>
                          <div>
                            <label className="block text-gray-500 text-sm mb-1.5">Telefon</label>
                            <input type="tel" className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg" placeholder="070-000 00 00" />
                          </div>
                        </div>
                        <div>
                          <label className="block text-gray-500 text-sm mb-1.5">Meddelande *</label>
                          <textarea rows={5} className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg resize-none" placeholder="Beskriv ditt projekt..." />
                        </div>
                        <button type="button" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#2563EB] text-white font-semibold rounded-lg">
                          Skicka offertförfrågan
                        </button>
                      </div>
                    }
                  >
                    <Suspense fallback={<div className="text-gray-500 py-8 text-center">Laddar formulär...</div>}>
                      <KontaktForm />
                    </Suspense>
                  </ClientOnly>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
